<template>
    <Paper v-on-clickaway="emitClose" class="verify-dialog-root p-4 noselect">
        <div v-if="isVerified()">
            <div class="d-flex align-items-center justify-content-center mb-2">
                <img
                    v-if="logo()"
                    :src="logo()"
                    :alt="nameAbbr()"
                    class="logo mr-1 noselect"
                />
                <h6 class="primary-color fat text-center mb-0">
                    {{
                        $t(
                            'shared.universityBadges.verificationDialog.verifiedStudent',
                            { schoolAbbr: nameAbbr() }
                        )
                    }}
                </h6>
            </div>
            <p class="text-small text-center">
                {{
                    $t(
                        'shared.universityBadges.verificationDialog.verificationDescription',
                        { schoolAbbr: nameAbbr(), email: email() }
                    )
                }}
            </p>
            <a
                v-if="showPlatformLink()"
                :href="linkToPlatform()"
                class="link-to-platform"
            >
                {{
                    $t(
                        'shared.universityBadges.verificationDialog.goToPlatform'
                    )
                }}
            </a>
        </div>
        <div
            v-else-if="connection !== null"
            class="d-flex flex-column align-items-center"
        >
            <h6 class="primary-color fat text-center mb-1">
                {{
                    $t(
                        'shared.universityBadges.verificationDialog.checkYourEmail',
                        { schoolAbbr: nameAbbr() }
                    )
                }}
            </h6>
            <p class="text-small text-center">
                {{
                    $t(
                        'shared.universityBadges.verificationDialog.sentEmailDescription',
                        { schoolAbbr: nameAbbr(), email: email() }
                    )
                }}
            </p>
            <div style="display: grid; grid-gap: 10px;">
                <BaseButton
                    variant="outline-dark"
                    :disabled="isLoading"
                    @click="resendVerification"
                >
                    <div
                        class="d-flex align-items-center justify-content-center"
                    >
                        <Spinner v-if="isLoading" size="sm" class="mr-2" />
                        {{
                            $t(
                                'shared.universityBadges.verificationDialog.resendEmail'
                            )
                        }}
                    </div>
                </BaseButton>
                <BaseButton
                    variant="outline-primary"
                    :disabled="isLoading"
                    @click="cancelVerification"
                >
                    <div
                        class="d-flex align-items-center justify-content-center"
                    >
                        <Spinner v-if="isLoading" size="sm" class="mr-2" />
                        {{
                            $t(
                                'shared.universityBadges.verificationDialog.cancelVerification'
                            )
                        }}
                    </div>
                </BaseButton>
            </div>
        </div>
        <div v-else>
            <h6 class="primary-color fat text-center mb-2">
                {{
                    $t(
                        'shared.universityBadges.verificationDialog.notYetVerified'
                    )
                }}
            </h6>
            <p class="text-small text-center">
                {{
                    $t(
                        'shared.universityBadges.verificationDialog.verifyYourAffiliation',
                        { schoolAbbr: nameAbbr() || 'university' }
                    )
                }}
            </p>
            <!-- <p class="text-small text-center">
                Not a student at {{ nameAbbr() || 'the university.' }}?
                <a>Click here</a>
            </p> -->
            <BaseButton
                class="w-100"
                variant="primary"
                @click="openVerifyDialog"
            >
                {{ $t('shared.universityBadges.verificationDialog.verify') }}
            </BaseButton>
        </div>
    </Paper>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import { getSubDomain, emailVerificationURL } from '~/utils/urlUtils';
import DialogService from '~/services/DialogService';
import i18n from '~/i18n';

export default {
    mixins: [clickaway],
    name: 'VerificationDialog',
    props: {
        connection: {
            type: Object,
            default: () => ({}),
        },
    },
    data: () => ({
        isLoading: false,
    }),
    methods: {
        emitClose() {
            this.$emit('close');
        },
        updateConnections() {
            this.$interfaces.university.connection.get({ forceUpdate: true });
        },
        isVerified() {
            return (this.connection || {}).isVerified;
        },
        school() {
            return this.connection && this.connection.school
                ? this.connection.school
                : {};
        },
        nameAbbr() {
            return !this.connection
                ? (getSubDomain() || '').toUpperCase()
                : (this.school().subDomain || '').toUpperCase();
        },
        email() {
            return ((this.connection || {}).studentEmail || '').toLowerCase();
        },
        logo() {
            return this.school().logo;
        },
        openVerifyDialog() {
            DialogService.openUniVerifyDialog();
            this.emitClose();
        },
        cancelVerification() {
            this.isLoading = true;
            this.$interfaces.university.connection
                .id((this.connection || {}).id)
                .delete({ body: {} })
                .then(res => {
                    this.updateConnections();
                    this.$interfaces.messageModule.addNewMessage({
                        title: i18n.t(
                            'components.miscellaneous.verifyModule.verificationCancelledTitle'
                        ),
                        message: i18n.t(
                            'components.miscellaneous.verifyModule.verificationCancelledMessage'
                        ),
                    });
                })
                .catch(res => {
                    this.$interfaces.messageModule.addNewMessage({
                        state: 'ERROR',
                        title: i18n.t(
                            'components.miscellaneous.verifyModule.cancelErrorTitle'
                        ),
                        message: i18n.t(
                            'components.miscellaneous.verifyModule.cancelErrorMessage'
                        ),
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                    this.emitClose();
                });
        },
        resendVerification() {
            this.isLoading = true;
            this.$interfaces.university.connection.confirm
                .post({
                    body: {
                        id: this.connection.id,
                        returnUrl: emailVerificationURL(),
                    },
                })
                .then(res => {
                    this.$interfaces.messageModule.addNewMessage({
                        title: i18n.t(
                            'components.miscellaneous.verifyModule.emailSent'
                        ),
                        message: i18n.t(
                            'components.miscellaneous.verifyModule.emailSentMessage'
                        ),
                    });
                })
                .catch(err => {
                    this.$interfaces.messageModule.addNewMessage({
                        state: 'ERROR',
                        title: i18n.t(
                            'components.miscellaneous.verifyModule.resendErrorTitle'
                        ),
                        message: i18n.t(
                            'components.miscellaneous.verifyModule.resendErrorMessage'
                        ),
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        linkToPlatform() {
            return IS_PROD
                ? `https://${this.nameAbbr()}.gethighered.global`
                : `https://${this.nameAbbr()}.gethighered-dev.global`;
        },
        showPlatformLink() {
            return getSubDomain() !== this.nameAbbr();
        },
    },
};
</script>

<style scoped>
.verify-dialog-root {
    position: absolute;
    top: 64px;
    right: 10px;
    min-width: 240px;
    max-width: 286px;
    height: auto;

    box-shadow: 3px 3px 5px 0px rgba(0, 0, 0, 0.4);
}

/* Text styles */
.fat {
    font-weight: 700 !important;
}
.text-small {
    font-size: 12px;
}

/* Image styles */
.logo {
    width: 24px;
    height: 24px;
    object-fit: scale-down;
}
.link-to-platform {
    text-align: center;
    text-decoration: none;
    display: block;
    color: var(--primary);
}
</style>
